import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown, ChevronUp, Users, Link2, Info, Lock, PieChart, Pencil, Building2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { MohitInvoice, InfluencerInvoice } from '../types/campaign';
import type { Client } from '../types/client';
import type { InvoiceLink } from '../services/invoiceLinkService';
import { formatMonthPt } from '../utils/avenca';
import { formatEuro } from '../utils/currency';
import { commissionShare } from '../utils/commissionCalculations';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { InvoiceRequestsBlock, type InvoiceRequestContext } from './invoice-requests/InvoiceRequestsBlock';
import { RowList, ExpandableRow, type RowTone } from './ui/ListRows';
import { FormBlock } from './ui/FormBlock';
import type { FulfilledInvoice } from '../services/invoiceRequestService';

export interface InvoiceInfluencerCommission {
  influencer_name: string;
  agreed_fee: number;
  commission_pct: number;
  commission_flat_fee: number;
  commission_type: 'percentage' | 'flat_fee';
}

export interface DeliverableItem {
  id: string;
  content: string;
  fee: number;
  commission: number;
  influencer_name: string;
  status: string;
  lockedByInvoiceId?: string;
  lockedByInvoiceNumber?: string | number;
}

interface MohitInvoicesProps {
  invoices: MohitInvoice[];
  onChange: (invoices: MohitInvoice[]) => void;
  clientDetails: Client | null;
  title?: string;
  totalAmount?: number;
  errorIds?: Set<string>;
  totalMismatch?: { expected: number; actual: number } | null;
  influencerCommissions?: InvoiceInfluencerCommission[];
  invoiceLinks?: InvoiceLink[];
  payableInvoices?: InfluencerInvoice[];
  allDeliverables?: DeliverableItem[];
  selectedDeliverables?: Record<string, string[]>;
  onDeliverablesChange?: (invoiceId: string, deliverableIds: string[]) => void;
  /**
   * Avença (monthly fee): invoices are one per month and keep their own
   * amounts — no tranche splitting; a button generates the next month.
   */
  avenca?: {
    summary: string;
    nextMonthLabel: string | null;
    onGenerateNext: () => void;
  } | null;
  /** Turns on "Pedir fatura" and the requests waiting for their invoice. */
  requests?: InvoiceRequestContext;
  /** The form opens here, not at its top. */
  scrollIntoViewOnMount?: boolean;
  /**
   * The campaign's commission. With several invoices, each one shows the
   * part of it that matches its share of the total.
   */
  totalCommission?: number;
}

interface LinkedPayableBreakdown {
  influencerName: string;
  payableTotal: number;
  invoiceNumber: string;
  status: string;
}

interface LinkBasedCommission {
  linkedPayables: LinkedPayableBreakdown[];
  totalPaidOut: number;
  commissionRetained: number;
  hasLinks: boolean;
}

/** An issued invoice's document data (number, dates, amount) is locked. */
export const isInvoiceLocked = (invoice: MohitInvoice) =>
  invoice.status !== 'Not Issued' && !!invoice.invoice_number;

interface SortableInvoiceItemProps {
  invoice: MohitInvoice;
  index: number;
  isExpanded: boolean;
  isOnlyInvoice: boolean;
  displayStatus: string;
  trancheLabel?: string;
  trancheAmount: number;
  totalAmount: number;
  hasError?: boolean;
  isManual: boolean;
  linkCommission?: LinkBasedCommission;
  /** This invoice's proportional part of the campaign commission. */
  commissionHint?: { value: number; share: number; total: number } | null;
  allDeliverables?: DeliverableItem[];
  selectedDeliverableIds?: string[];
  onDeliverablesChange?: (invoiceId: string, deliverableIds: string[]) => void;
  expandedDeliverableLists: Set<string>;
  onToggleDeliverableList: (invoiceId: string) => void;
  onToggleExpand: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, field: string, value: string | number) => void;
  onAmountChange: (id: string, value: number) => void;
  onSetDueDatePreset: (invoiceId: string, invoice: MohitInvoice, days: number) => void;
  t: (key: string) => string;
  ts: (key: string) => string;
}

function SortableInvoiceItem({
  invoice,
  index,
  isExpanded,
  isOnlyInvoice,
  displayStatus,
  trancheLabel,
  trancheAmount,
  totalAmount,
  hasError,
  isManual,
  linkCommission,
  commissionHint,
  allDeliverables,
  selectedDeliverableIds,
  onDeliverablesChange,
  expandedDeliverableLists,
  onToggleDeliverableList,
  onToggleExpand,
  onDelete,
  onUpdate,
  onAmountChange,
  onSetDueDatePreset,
  t,
  ts,
}: SortableInvoiceItemProps) {
  // Issued invoices show their document data read-only until "Editar"
  const [unlocked, setUnlocked] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: invoice.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const locked = isInvoiceLocked(invoice) && !unlocked;
  const fmt = formatEuro;
  const day = (iso: string) => iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-PT') : '';
  // The even split is only a forecast: an invoice already issued has its own
  // amount, and showing the forecast would hide that it was never filled in
  const missingAmount = !(invoice.amount_received > 0) && invoice.status !== 'Not Issued' && invoice.status !== 'Cancelled';
  const shownAmount = invoice.amount_received > 0 ? invoice.amount_received : missingAmount ? 0 : trancheAmount;
  const summary = [
    invoice.purchase_order && `${t('mohitInvoice.short.po')} ${invoice.purchase_order}`,
    invoice.issued_date && t('mohitInvoice.issuedOn').replace('{0}', day(invoice.issued_date)),
    invoice.due_date && t('mohitInvoice.dueOn').replace('{0}', day(invoice.due_date)),
  ].filter(Boolean).join(' · ');

  // Compact field: label above, input (or the locked value) below
  const field = (label: React.ReactNode, editor: React.ReactNode, value: string, isLocked = locked) => (
    <div className="min-w-0">
      <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">{label}</div>
      {isLocked
        ? <div className="cm-readonly !h-9 truncate" title={value || undefined}>{value || '—'}</div>
        : editor}
    </div>
  );

  const tone: RowTone =
    displayStatus === 'Paid' ? 'success'
    : displayStatus === 'Overdue' ? 'danger'
    : displayStatus === 'Issued' ? 'info'
    : 'neutral';

  return (
    <ExpandableRow
      nodeRef={setNodeRef}
      style={style}
      isDragging={isDragging}
      dragHandle={isOnlyInvoice ? null : { ...attributes, ...listeners }}
      tone={tone}
      isExpanded={isExpanded}
      onToggle={() => onToggleExpand(invoice.id)}
      hasError={hasError}
      onDelete={isOnlyInvoice ? undefined : () => onDelete(invoice.id)}
      deleteLabel={t('mohitInvoice.delete')}
      aside={missingAmount ? (
        <span className="text-xs font-semibold text-amber-700">{t('mohitInvoice.noAmount')}</span>
      ) : shownAmount > 0 ? (
        commissionHint ? (
          <span className="flex flex-col items-end leading-tight">
            <span>{fmt(shownAmount)}</span>
            <span className="text-[11px] font-medium text-emerald-700" title={t('mohitInvoice.commissionHintTitle')}>
              {t('mohitInvoice.commissionShort').replace('{0}', fmt(commissionHint.value))}
            </span>
          </span>
        ) : fmt(shownAmount)
      ) : ''}
      summary={<>
        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
          {invoice.invoice_number ? `Nº ${invoice.invoice_number}` : t('mohitInvoice.invoiceNumber').replace('{0}', String(index + 1))}
        </span>
        {trancheLabel && (
          <span className="px-1.5 py-0.5 text-[11px] font-semibold rounded bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
            {trancheLabel}
          </span>
        )}
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
          displayStatus === 'Paid' ? 'bg-green-100 text-green-800' :
          displayStatus === 'Issued' ? 'bg-blue-100 text-blue-800' :
          displayStatus === 'Overdue' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-700'
        }`}>
          {ts(displayStatus)}
        </span>
        {summary && <span className="hidden md:inline text-xs text-gray-500 truncate min-w-0">{summary}</span>}
      </>}
    >
          {isInvoiceLocked(invoice) && (
            <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" />
                {locked ? t('mohitInvoice.lockedHint') : t('mohitInvoice.editingHint')}
              </span>
              <button
                type="button"
                onClick={() => setUnlocked(u => !u)}
                className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline"
              >
                {locked ? <><Pencil className="w-3.5 h-3.5" /> {t('mohitInvoice.edit')}</> : <><Lock className="w-3.5 h-3.5" /> {t('mohitInvoice.lock')}</>}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2.5">
            {field(
              t('mohitInvoice.short.number'),
              <input
                type="text"
                value={invoice.invoice_number}
                onChange={(e) => onUpdate(invoice.id, 'invoice_number', e.target.value)}
                placeholder={t('mohitInvoice.numberPlaceholder')}
                className="cm-input !h-9"
              />,
              invoice.invoice_number
            )}
            {field(
              t('mohitInvoice.short.po'),
              <input
                type="text"
                value={invoice.purchase_order}
                onChange={(e) => onUpdate(invoice.id, 'purchase_order', e.target.value)}
                placeholder={t('mohitInvoice.purchaseOrderPlaceholder')}
                className="cm-input !h-9"
              />,
              invoice.purchase_order
            )}
            {/* Payment lifecycle stays editable on a locked invoice */}
            {field(
              <>{t('mohitInvoice.short.status')} <span className="text-red-500">*</span></>,
              <select
                value={invoice.status}
                onChange={(e) => onUpdate(invoice.id, 'status', e.target.value)}
                required
                className="cm-select !h-9"
              >
                <option value="Not Issued">{ts('Not Issued')}</option>
                <option value="Issued">{ts('Issued')}</option>
                <option value="Paid">{ts('Paid')}</option>
                {/* Vencida is shown automatically from the due date; Anulada was
                    never used — both stay readable on old invoices only */}
                {invoice.status === 'Overdue' && <option value="Overdue">{ts('Overdue')}</option>}
                {invoice.status === 'Cancelled' && <option value="Cancelled">{ts('Cancelled')}</option>}
              </select>,
              ts(invoice.status),
              false
            )}
            {field(
              <>
                {t('mohitInvoice.short.amount')}
                {invoice.status === 'Paid' && <span className="text-red-500">*</span>}
                {totalAmount > 0 && invoice.amount_received > 0 && (
                  <span className="font-normal text-gray-400">{((invoice.amount_received / totalAmount) * 100).toFixed(0)}%</span>
                )}
                {isManual && !locked && <span className="font-normal text-gray-400">manual</span>}
              </>,
              <input
                type="number"
                data-required-field={`mohit-invoice-amount-${invoice.id}`}
                value={invoice.amount_received || ''}
                onChange={(e) => onAmountChange(invoice.id, parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                placeholder={t('mohitInvoice.amountReceivedPlaceholder')}
                className={`cm-input cm-numeric !h-9 ${
                  hasError ? 'border-red-500 ring-2 ring-red-200 focus-visible:ring-red-500 focus-visible:border-red-500' : ''
                }`}
              />,
              invoice.amount_received > 0 ? fmt(invoice.amount_received) : ''
            )}
            {field(
              t('mohitInvoice.short.issued'),
              <input
                type="date"
                value={invoice.issued_date}
                onChange={(e) => onUpdate(invoice.id, 'issued_date', e.target.value)}
                className="cm-date !h-9"
              />,
              day(invoice.issued_date)
            )}
            {field(
              <>
                {t('mohitInvoice.short.due')}
                {!locked && [30, 60, 90].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => onSetDueDatePreset(invoice.id, invoice, days)}
                    className="px-1.5 rounded-full border border-gray-300 text-[10px] font-semibold text-gray-600 hover:border-brand-ink hover:text-brand-ink"
                    title={t(`mohitInvoice.dueDatePreset${days}`)}
                  >
                    {days}
                  </button>
                ))}
              </>,
              <input
                type="date"
                value={invoice.due_date}
                onChange={(e) => onUpdate(invoice.id, 'due_date', e.target.value)}
                className="cm-date !h-9"
              />,
              day(invoice.due_date)
            )}
            {invoice.status === 'Paid' && field(
              <>{t('mohitInvoice.short.paid')} <span className="text-red-500">*</span></>,
              <input
                type="date"
                data-required-field={`mohit-invoice-date-${invoice.id}`}
                value={invoice.payment_received_date}
                onChange={(e) => onUpdate(invoice.id, 'payment_received_date', e.target.value)}
                className={`cm-date !h-9 ${
                  !invoice.payment_received_date
                    ? 'border-red-500 ring-2 ring-red-200 focus-visible:ring-red-500 focus-visible:border-red-500'
                    : ''
                }`}
              />,
              day(invoice.payment_received_date),
              false
            )}
          </div>

          {displayStatus === 'Overdue' && invoice.status === 'Issued' && (
            <p className="text-xs text-red-600">{t('mohitInvoice.overdueNotice')}</p>
          )}
          {invoice.status === 'Paid' && hasError && (
            <p className="text-xs text-red-600 font-medium">{t('mohitInvoice.amountReceivedRequired')}</p>
          )}
          {commissionHint && (
            <p className="text-xs text-emerald-700">
              {t('mohitInvoice.commissionHint')
                .replace('{0}', fmt(commissionHint.value))
                .replace('{1}', new Intl.NumberFormat('pt-PT', { style: 'percent', maximumFractionDigits: 1 }).format(commissionHint.share))
                .replace('{2}', fmt(commissionHint.total))}
            </p>
          )}

          {/* Deliverable items selector */}
          {allDeliverables && allDeliverables.length > 0 && onDeliverablesChange && (() => {
            const fmt = formatEuro;
            const isListExpanded = expandedDeliverableLists.has(invoice.id);
            const toggleList = () => onToggleDeliverableList(invoice.id);
            const selectedFeeTotal = allDeliverables
              .filter(d => selectedDeliverableIds?.includes(d.id))
              .reduce((s, d) => s + d.fee, 0);
            const selectedCommissionTotal = allDeliverables
              .filter(d => selectedDeliverableIds?.includes(d.id))
              .reduce((s, d) => s + (d.commission || 0), 0);
            return (
              <div className="mt-3 pt-3 border-t border-gray-100">
                {/* Collapsible header */}
                <button
                  type="button"
                  onClick={toggleList}
                  className="w-full flex items-center gap-1.5 mb-2 group focus:outline-none"
                >
                  <Link2 className="w-3.5 h-3.5 text-accent-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-gray-700">Items nesta fatura</span>
                  <span className="text-xs text-gray-400 ml-1">({allDeliverables.length})</span>
                  {selectedDeliverableIds && selectedDeliverableIds.length > 0 && (
                    <span className="text-xs text-accent-600 font-medium ml-2">
                      Total: {fmt(selectedFeeTotal)}
                      {selectedCommissionTotal > 0 && (
                        <span className="text-gray-500"> (Comissão: {fmt(selectedCommissionTotal)})</span>
                      )}
                    </span>
                  )}
                  <span className="ml-auto text-gray-400 group-hover:text-gray-600 transition-colors">
                    {isListExpanded
                      ? <ChevronUp className="w-3.5 h-3.5" />
                      : <ChevronDown className="w-3.5 h-3.5" />
                    }
                  </span>
                </button>
                {/* Collapsible body */}
                <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isListExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2 max-h-[200px] overflow-y-auto space-y-1">
                    {Object.entries(
                      allDeliverables.reduce<Record<string, DeliverableItem[]>>((groups, d) => {
                        const key = d.influencer_name;
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(d);
                        return groups;
                      }, {})
                    ).map(([influencerName, items]) => (
                      <div key={influencerName}>
                        <p className="cm-caption px-1 pt-1">{influencerName}</p>
                        {items.map(item => {
                          const isSelected = selectedDeliverableIds?.includes(item.id) || false;
                          const isLocked = !!item.lockedByInvoiceId && item.lockedByInvoiceId !== invoice.id;
                          return (
                            <label
                              key={item.id}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
                                isLocked
                                  ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                                  : isSelected
                                  ? 'bg-accent-50 border border-accent-200 cursor-pointer'
                                  : 'hover:bg-gray-100 cursor-pointer'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected || isLocked}
                                disabled={isLocked}
                                onChange={(e) => {
                                  if (isLocked) return;
                                  const currentIds = selectedDeliverableIds || [];
                                  const newIds = e.target.checked
                                    ? [...currentIds, item.id]
                                    : currentIds.filter(id => id !== item.id);
                                  onDeliverablesChange(invoice.id, newIds);
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-accent-600 focus:ring-accent-500 disabled:opacity-50"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  {isLocked && <Lock className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                                  <span className="text-xs text-gray-700 truncate">{item.content || 'Sem descricao'}</span>
                                </div>
                                {isLocked && item.lockedByInvoiceNumber != null && (
                                  <span className="text-xs text-gray-400">Já na fatura #{item.lockedByInvoiceNumber}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-xs tabular-nums text-gray-700 font-medium">
                                  {fmt(item.fee)}
                                </span>
                                {item.commission > 0 && (
                                  <div className="relative group">
                                    <Info className="w-3 h-3 text-gray-400 cursor-help" />
                                    <div className="absolute right-0 bottom-full mb-1.5 w-max max-w-[180px] bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 leading-snug opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg">
                                      Inclui comissão de {fmt(item.commission)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Expected cost & margin from deliverables — shown immediately */}
          {allDeliverables && selectedDeliverableIds && selectedDeliverableIds.length > 0 && invoice.amount_received > 0 && (() => {
            const fmt = formatEuro;
            const selectedItems = allDeliverables.filter(d => selectedDeliverableIds.includes(d.id));
            const expectedCreatorCost = selectedItems.reduce((s, d) => s + (d.fee - (d.commission || 0)), 0);
            const expectedMargin = invoice.amount_received - expectedCreatorCost;
            return (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <PieChart className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-700">Previsão de margem</span>
                </div>
                <div className="bg-blue-50 rounded-lg border border-blue-100 p-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Receita</p>
                    <p className="text-xs font-bold text-gray-900 tabular-nums">{fmt(invoice.amount_received)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Custo esperado</p>
                    <p className="text-xs font-bold text-gray-900 tabular-nums">{fmt(expectedCreatorCost)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Margem esperada</p>
                    <p className={`text-xs font-bold tabular-nums ${expectedMargin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {fmt(expectedMargin)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Commission breakdown based on linked payable invoices */}
          {linkCommission && linkCommission.hasLinks && invoice.amount_received > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-semibold text-gray-700">Comissões nesta fatura</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {linkCommission.linkedPayables.length} fatura{linkCommission.linkedPayables.length !== 1 ? 's' : ''} ligada{linkCommission.linkedPayables.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                {/* Mobile: stacked cards */}
                <div className="sm:hidden divide-y divide-gray-100">
                  {linkCommission.linkedPayables.map((item, idx) => (
                    <div key={idx} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-800">{item.influencerName}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          item.status === 'Paid' ? 'bg-green-100 text-green-800' :
                          item.status === 'Issued' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>{item.status === 'Paid' ? 'Paga' : item.status === 'Issued' ? 'Emitida' : item.status}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">Fatura #{item.invoiceNumber}</span>
                        <span className="text-xs tabular-nums font-medium text-gray-700">
                          {formatEuro(item.payableTotal)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-2 bg-gray-100/60">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">Comissão retida</span>
                      <span className="text-xs tabular-nums font-bold text-emerald-700">
                        {formatEuro(linkCommission.commissionRetained)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-gray-500">Total pago</span>
                      <span className="text-xs tabular-nums text-gray-500">
                        {formatEuro(linkCommission.totalPaidOut)}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Desktop: table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-100/60">
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Influenciador</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Fatura</th>
                      <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-1.5 text-right font-semibold text-gray-500">Valor pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkCommission.linkedPayables.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-1.5 font-medium text-gray-800">{item.influencerName}</td>
                        <td className="px-3 py-1.5 text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Link2 className="w-3 h-3 text-gray-400" />
                            #{item.invoiceNumber}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                            item.status === 'Paid' ? 'bg-green-100 text-green-700' :
                            item.status === 'Issued' ? 'bg-accent-100 text-accent-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{item.status === 'Paid' ? 'Paga' : item.status === 'Issued' ? 'Emitida' : item.status}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                          {formatEuro(item.payableTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-100/60">
                      <td className="px-3 py-1.5 font-bold text-gray-700" colSpan={3}>Comissão retida</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-bold text-emerald-700">
                        {formatEuro(linkCommission.commissionRetained)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            </div>
          )}
          {/* No links yet - show hint */}
          {linkCommission && !linkCommission.hasLinks && invoice.amount_received > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 italic">
                Nenhuma fatura de pagamento ligada. Ligue faturas na aba "Faturas a Pagar" para ver a comissão real.
              </p>
            </div>
          )}
    </ExpandableRow>
  );
}

export function MohitInvoices({ invoices, onChange, clientDetails, title, totalAmount = 0, errorIds, totalMismatch, influencerCommissions, invoiceLinks, payableInvoices, allDeliverables, selectedDeliverables, onDeliverablesChange, avenca, requests, scrollIntoViewOnMount, totalCommission = 0 }: MohitInvoicesProps) {
  const { t, ts } = useLanguage();
  const { toast } = useModal();
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollIntoViewOnMount) return;
    const timer = window.setTimeout(() => rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 150);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(
    new Set(invoices.length === 1 && !isInvoiceLocked(invoices[0]) ? [invoices[0]?.id] : [])
  );
  const [numberOfTranches, setNumberOfTranches] = useState(() => Math.max(1, invoices.length));
  const [expandedDeliverableLists, setExpandedDeliverableLists] = useState<Set<string>>(
    () => new Set(invoices.map(inv => inv.id))
  );
  const toggleDeliverableList = (invoiceId: string) => setExpandedDeliverableLists(prev => {
    const next = new Set(prev);
    if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
    return next;
  });
  const [manualAmountIds, setManualAmountIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    invoices.forEach(inv => {
      if ((inv.amount_received || 0) > 0) initial.add(inv.id);
    });
    return initial;
  });
  const lastTotalAmountRef = useRef<number>(totalAmount);

  useEffect(() => {
    if (errorIds && errorIds.size > 0) {
      setExpandedInvoices(prev => {
        const next = new Set(prev);
        errorIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [errorIds]);

  const computePerTranche = (total: number, count: number) =>
    total > 0 && count >= 1 ? Math.round((total / count) * 100) / 100 : 0;

  const trancheAmount = computePerTranche(totalAmount, numberOfTranches);

  // With several invoices, each shows its part of the commission. Monthly
  // fees (avenças) bill month by month, so a share of the total means nothing.
  const commissionHintFor = (invoice: MohitInvoice) => {
    if (invoices.length < 2 || avenca) return null;
    const amount = invoice.amount_received > 0 ? invoice.amount_received : trancheAmount;
    const share = commissionShare(amount, totalAmount, totalCommission);
    return share ? { ...share, total: totalCommission } : null;
  };

  const redistributeAmounts = (
    list: MohitInvoice[],
    _count: number,
    totalOverride?: number,
    manualOverrideIds?: Set<string>
  ): MohitInvoice[] => {
    const baseTotal = totalOverride ?? totalAmount;
    const manualIds = manualOverrideIds ?? manualAmountIds;
    const lockedIndexes = new Set<number>();
    list.forEach((inv, idx) => {
      if (inv.status === 'Paid' || manualIds.has(inv.id)) lockedIndexes.add(idx);
    });
    const flexibleIndexes: number[] = [];
    list.forEach((_, idx) => {
      if (!lockedIndexes.has(idx)) flexibleIndexes.push(idx);
    });
    if (flexibleIndexes.length === 0) {
      return list;
    }
    const lockedSum = list.reduce(
      (sum, inv, idx) => (lockedIndexes.has(idx) ? sum + (inv.amount_received || 0) : sum),
      0
    );
    const remaining = Math.max(0, Math.round((baseTotal - lockedSum) * 100) / 100);
    if (remaining === 0) {
      return list.map((inv, idx) =>
        lockedIndexes.has(idx) ? inv : { ...inv, amount_received: 0 }
      );
    }
    const evenShare = Math.round((remaining / flexibleIndexes.length) * 100) / 100;
    const assigned = evenShare * flexibleIndexes.length;
    const remainder = Math.round((remaining - assigned) * 100) / 100;
    const lastFlexible = flexibleIndexes[flexibleIndexes.length - 1];
    return list.map((inv, idx) => {
      if (lockedIndexes.has(idx)) return inv;
      const value = idx === lastFlexible ? Math.round((evenShare + remainder) * 100) / 100 : evenShare;
      return { ...inv, amount_received: value };
    });
  };

  useEffect(() => {
    if (lastTotalAmountRef.current === totalAmount) return;
    lastTotalAmountRef.current = totalAmount;
    // Monthly invoices keep their own amounts (a fee raise mid-year must not
    // be spread over earlier months)
    if (avenca) return;
    if (invoices.length === 0) return;
    const hasNonPaid = invoices.some(inv => inv.status !== 'Paid');
    if (!hasNonPaid) return;
    const updated = redistributeAmounts(invoices, numberOfTranches, totalAmount);
    const changed = updated.some((inv, i) => inv.amount_received !== invoices[i].amount_received);
    if (changed) onChange(updated);
  }, [totalAmount]);

  // Validate: warn if total invoiced amount exceeds the sum of influencer agreed fees
  const lastCommWarningRef = useRef<string>('');
  useEffect(() => {
    if (!influencerCommissions || influencerCommissions.length === 0) return;
    const sumAgreedFees = influencerCommissions.reduce((s, inf) => s + inf.agreed_fee, 0);
    const denominator = totalAmount > 0 ? totalAmount : sumAgreedFees;
    if (denominator <= 0) return;

    const totalAllocated = invoices.reduce((s, inv) => s + (inv.amount_received || 0), 0);
    const totalRatio = totalAllocated / denominator;
    if (totalRatio <= 1.01) return;

    const warningKey = `${totalAllocated.toFixed(2)}-${denominator.toFixed(2)}`;
    if (lastCommWarningRef.current === warningKey) return;
    lastCommWarningRef.current = warningKey;

    toast.warning(
      `Atenção: o total faturado (${formatEuro(totalAllocated)}) excede o valor total atribuído (${formatEuro(denominator)}). As comissões atribuídas ultrapassam o valor real.`
    );
  }, [invoices, influencerCommissions, totalAmount]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getDisplayStatus = (invoice: MohitInvoice) => {
    if (invoice.status === 'Paid' || invoice.status === 'Cancelled') {
      return invoice.status;
    }

    if (invoice.due_date && invoice.status === 'Issued') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(invoice.due_date);
      dueDate.setHours(0, 0, 0, 0);

      if (dueDate < today) {
        return 'Overdue';
      }
    }

    return invoice.status;
  };

  const computeRemainingAmount = () => {
    const allocated = invoices.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
    const diff = Math.round((totalAmount - allocated) * 100) / 100;
    return diff > 0 ? diff : 0;
  };

  const addInvoice = () => {
    const newCount = invoices.length + 1;
    const newInvoice: MohitInvoice = {
      id: `temp-${crypto.randomUUID()}`,
      status: 'Not Issued',
      invoice_number: '',
      purchase_order: '',
      issued_date: '',
      due_date: '',
      amount_received: computeRemainingAmount(),
      payment_received_date: '',
      client_invoice_number: '',
    };
    setNumberOfTranches(newCount);
    onChange([...invoices, newInvoice]);
    setExpandedInvoices(new Set([newInvoice.id]));
  };

  const addTranche = () => {
    const newCount = numberOfTranches + 1;
    const newInvoice: MohitInvoice = {
      id: `temp-${crypto.randomUUID()}`,
      status: 'Not Issued',
      invoice_number: '',
      purchase_order: '',
      issued_date: '',
      due_date: '',
      amount_received: computeRemainingAmount(),
      payment_received_date: '',
      client_invoice_number: '',
    };
    setNumberOfTranches(newCount);
    onChange([...invoices, newInvoice]);
    setExpandedInvoices(new Set([newInvoice.id]));
  };

  // A requested invoice arrived: fill the planned line it stands for (same
  // amount, else an empty one), or add it
  const applyReceivedInvoice = (requestedAmount: number, received: FulfilledInvoice) => {
    const isOpen = (inv: MohitInvoice) => inv.status === 'Not Issued' && !inv.invoice_number;
    let target = invoices.findIndex(inv => isOpen(inv) && Math.abs((inv.amount_received || 0) - requestedAmount) < 0.01);
    if (target < 0) target = invoices.findIndex(inv => isOpen(inv) && !(inv.amount_received > 0));
    const fields: Partial<MohitInvoice> = {
      status: 'Issued',
      invoice_number: received.invoice_number,
      issued_date: received.issued_date,
      due_date: received.due_date,
      amount_received: received.net_amount ?? requestedAmount,
    };
    let updated: MohitInvoice[];
    let id: string;
    if (target >= 0) {
      id = invoices[target].id;
      updated = invoices.map((inv, i) => i === target
        ? { ...inv, ...fields, purchase_order: received.purchase_order || inv.purchase_order }
        : inv);
    } else {
      id = `temp-${crypto.randomUUID()}`;
      updated = [...invoices, {
        id,
        payment_received_date: '',
        client_invoice_number: '',
        ...fields,
        purchase_order: received.purchase_order,
      } as MohitInvoice];
    }
    setManualAmountIds(prev => new Set(prev).add(id));
    setNumberOfTranches(updated.length);
    onChange(updated);
  };

  const removeTranche = () => {
    if (invoices.length <= 1) return;
    const newCount = Math.max(1, numberOfTranches - 1);
    const sliced = invoices.slice(0, -1);
    const updated = redistributeAmounts(sliced, newCount);
    setNumberOfTranches(newCount);
    onChange(updated);
  };

  const updateInvoice = (id: string, field: string, value: string | number) => {
    const updated = invoices.map(inv => {
      if (inv.id !== id) return inv;
      const patch: Partial<MohitInvoice> = { [field]: value };
      if (field === 'status' && value === 'Paid') {
        if (!inv.amount_received || inv.amount_received === 0) {
          patch.amount_received = trancheAmount;
        }
      } else if (field === 'status' && value !== 'Paid') {
        patch.payment_received_date = '';
      }
      return { ...inv, ...patch };
    });
    onChange(updated);
  };

  const handleAmountChange = (id: string, value: number) => {
    setManualAmountIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const updated = invoices.map(inv =>
      inv.id === id ? { ...inv, amount_received: value } : inv
    );
    onChange(updated);
  };

  const distributeEvenly = () => {
    setManualAmountIds(new Set());
    const updated = redistributeAmounts(invoices, invoices.length, totalAmount, new Set());
    onChange(updated);
  };

  const distributeRemaining = () => {
    const updated = redistributeAmounts(invoices, invoices.length, totalAmount);
    onChange(updated);
  };

  const calculateDueDate = (issueDate: string, days: number): string => {
    if (!issueDate) return '';
    const date = new Date(issueDate);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const setDueDatePreset = (invoiceId: string, invoice: MohitInvoice, days: number) => {
    if (!invoice.issued_date) {
      toast.warning(t('mohitInvoice.setIssueDateFirst'));
      return;
    }
    const dueDate = calculateDueDate(invoice.issued_date, days);
    updateInvoice(invoiceId, 'due_date', dueDate);
  };

  const deleteInvoice = (id: string) => {
    if (invoices.length === 1) return;
    const filtered = invoices.filter(inv => inv.id !== id);
    const nextManual = new Set(manualAmountIds);
    nextManual.delete(id);
    setManualAmountIds(nextManual);
    const newCount = Math.max(1, numberOfTranches - 1);
    const updated = redistributeAmounts(filtered, newCount, totalAmount, nextManual);
    setNumberOfTranches(newCount);
    onChange(updated);
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = invoices.findIndex(inv => inv.id === active.id);
      const newIndex = invoices.findIndex(inv => inv.id === over.id);
      const reordered = arrayMove(invoices, oldIndex, newIndex);
      onChange(reordered);
    }
  };

  return (
    <FormBlock title={title || t('mohitInvoice.title')}>
    <div ref={rootRef} className="space-y-4 scroll-mt-16">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto min-w-0">
          {clientDetails && (
            <p
              className="flex items-center gap-1.5 text-xs text-gray-500 truncate"
              title={[clientDetails.name, clientDetails.vat_number && `${t('clients.vat')} ${clientDetails.vat_number}`, clientDetails.address, clientDetails.contact_info].filter(Boolean).join(' · ')}
            >
              <Building2 className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium text-gray-700">{clientDetails.name}</span>
              {clientDetails.vat_number && <span>· {t('clients.vat')} {clientDetails.vat_number}</span>}
              {clientDetails.address && <span className="truncate">· {clientDetails.address}</span>}
            </p>
          )}
        </div>
        {requests && (
          <InvoiceRequestsBlock
            context={requests}
            client={clientDetails}
            totalAmount={totalAmount}
            invoices={invoices}
            onInvoiceReceived={applyReceivedInvoice}
          />
        )}
        <button
          type="button"
          onClick={addInvoice}
          className={requests ? 'cm-btn cm-btn-ghost !h-9 !px-3' : 'cm-btn-add'}
        >
          <Plus className="w-4 h-4" />
          {requests ? t('mohitInvoice.addManually') : t('mohitInvoice.addInvoice')}
        </button>
      </div>

      {avenca && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-700">{avenca.summary}</span>
          {avenca.nextMonthLabel ? (
            <button
              type="button"
              onClick={avenca.onGenerateNext}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-brand-ink text-white hover:bg-black transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('avenca.generate').replace('{0}', avenca.nextMonthLabel)}
            </button>
          ) : (
            <span className="text-xs font-medium text-emerald-700">{t('avenca.upToDate')}</span>
          )}
        </div>
      )}

      {invoices.length === 0 && (
        <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-4 text-center">
          {requests
            ? 'Ainda sem faturas. Quando for altura de faturar, usa "Pedir fatura".'
            : 'Ainda sem faturas.'}
        </p>
      )}

      {!avenca && totalAmount > 0 && invoices.length > 0 && (() => {
        const allocated = invoices.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
        const diff = Math.round((totalAmount - allocated) * 100) / 100;
        const balanceTone = diff === 0
          ? 'text-emerald-700'
          : diff > 0
            ? 'text-amber-700'
            : 'text-red-700';
        const balanceLabel = diff === 0
          ? t('mohitInvoice.fullyAllocated')
          : diff > 0
            ? `${t('mohitInvoice.remaining')}: ${formatEuro(diff)}`
            : `${t('mohitInvoice.overAllocated')}: ${formatEuro(Math.abs(diff))}`;
        return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1">
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">
              {t('mohitInvoice.tranches')}
            </label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={removeTranche}
                disabled={invoices.length <= 1}
                className="w-6 h-6 flex items-center justify-center text-sm font-medium bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                -
              </button>
              <span className="w-8 text-center text-sm font-semibold text-gray-900">
                {numberOfTranches}
              </span>
              <button
                type="button"
                onClick={addTranche}
                className="w-6 h-6 flex items-center justify-center text-sm font-medium bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                +
              </button>
            </div>
            <span className="text-xs text-gray-500">|</span>
            <button
              type="button"
              onClick={distributeEvenly}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline transition-colors"
              title={t('mohitInvoice.distributeEvenlyHint')}
            >
              {t('mohitInvoice.distributeEvenly')}
            </button>
            <button
              type="button"
              onClick={distributeRemaining}
              disabled={diff === 0}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline transition-colors disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
              title={t('mohitInvoice.distributeRemainingHint')}
            >
              {t('mohitInvoice.distributeRemaining')}
            </button>
            <span className="text-xs text-gray-500">|</span>
            <span className="text-xs text-gray-600">
              {formatEuro(allocated)} / {formatEuro(totalAmount)}
            </span>
            <span className={`text-xs font-medium ${balanceTone}`}>
              {balanceLabel}
            </span>
          </div>
        );
      })()}

      {totalMismatch && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-xs text-red-800">
          <p className="font-semibold mb-0.5">{t('mohitInvoice.totalMismatchTitle')}</p>
          <p>
            {t('mohitInvoice.totalMismatchDetail')}:{' '}
            <span className="font-semibold">{formatEuro(totalMismatch.expected)}</span>
            {' | '}
            {t('mohitInvoice.totalMismatchEntered')}:{' '}
            <span className="font-semibold">{formatEuro(totalMismatch.actual)}</span>
          </p>
        </div>
      )}


      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={invoices.map(inv => inv.id)}
          strategy={verticalListSortingStrategy}
        >
          {invoices.length > 0 && <RowList>
            {invoices.map((invoice, index) => {
              const isExpanded = expandedInvoices.has(invoice.id);
              const isOnlyInvoice = invoices.length === 1;
              const displayStatus = getDisplayStatus(invoice);

              const trancheLabel = invoice.period
                ? formatMonthPt(invoice.period)
                : numberOfTranches > 1
                  ? `${t('mohitInvoice.trancheLabel')} ${index + 1}/${numberOfTranches}`
                  : undefined;

              // Compute commission based on linked payable invoices
              let linkCommission: LinkBasedCommission | undefined;

              if (invoiceLinks && payableInvoices) {
                const linksForThisInvoice = invoiceLinks.filter(l => l.client_invoice_id === invoice.id);
                const hasLinks = linksForThisInvoice.length > 0;

                if (hasLinks) {
                  const linkedPayables: LinkedPayableBreakdown[] = [];
                  for (const link of linksForThisInvoice) {
                    const payable = payableInvoices.find(p => p.id === link.payable_invoice_id);
                    if (!payable) continue;
                    linkedPayables.push({
                      influencerName: payable.source_name || 'Desconhecido',
                      payableTotal: payable.amount || 0,
                      invoiceNumber: payable.invoice_number || '',
                      status: payable.status || '',
                    });
                  }

                  const totalPaidOut = linkedPayables.reduce((s, p) => s + p.payableTotal, 0);
                  const selectedIds = selectedDeliverables?.[invoice.id] ?? [];
                  const commissionRetained = allDeliverables
                    ? allDeliverables.filter(d => selectedIds.includes(d.id)).reduce((s, d) => s + (d.commission || 0), 0)
                    : Math.max(0, (invoice.amount_received || 0) - totalPaidOut);

                  linkCommission = { linkedPayables, totalPaidOut, commissionRetained, hasLinks: true };
                } else {
                  linkCommission = { linkedPayables: [], totalPaidOut: 0, commissionRetained: 0, hasLinks: false };
                }
              }

              return (
                <SortableInvoiceItem
                  key={invoice.id}
                  invoice={invoice}
                  index={index}
                  isExpanded={isExpanded}
                  isOnlyInvoice={isOnlyInvoice}
                  displayStatus={displayStatus}
                  trancheLabel={trancheLabel}
                  trancheAmount={trancheAmount}
                  totalAmount={totalAmount}
                  hasError={errorIds?.has(invoice.id)}
                  isManual={manualAmountIds.has(invoice.id)}
                  linkCommission={linkCommission}
                  commissionHint={commissionHintFor(invoice)}
                  allDeliverables={allDeliverables}
                  selectedDeliverableIds={selectedDeliverables?.[invoice.id]}
                  onDeliverablesChange={onDeliverablesChange}
                  expandedDeliverableLists={expandedDeliverableLists}
                  onToggleDeliverableList={toggleDeliverableList}
                  onToggleExpand={toggleExpand}
                  onAmountChange={handleAmountChange}
                  onDelete={deleteInvoice}
                  onUpdate={updateInvoice}
                  onSetDueDatePreset={setDueDatePreset}
                  t={t}
                  ts={ts}
                />
              );
            })}
          </RowList>}
        </SortableContext>
      </DndContext>
    </div>
    </FormBlock>
  );
}
